"use client";
import {
  Bot,
  ChevronRight,
  Copy,
  Database,
  Download,
  Eye,
  Fingerprint,
  Info,
  LockKeyhole,
  Network,
  Radar,
  RefreshCw,
  ScanLine,
  ShieldCheck,
  Terminal,
  Zap,
} from "lucide-react";
import {
  type Finding,
  type ScanReport,
} from "../../../lib/types";
import {
  formatBytes,
  formatDate,
  formatDuration,
  truncateMiddle,
  severityOrder,
} from "../../shared/format";
import {
  StatusDot,
} from "../../shared/primitives";
import {
  type WorkspaceView,
} from "../../shared/types";
import {
  verdictTone,
  verdictIcon,
  verdictCopy,
} from "../../shared/verdict";


function ScoreRing({ report, compact = false }: { report: ScanReport; compact?: boolean }) {
  const radius = compact ? 28 : 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (report.score / 100) * circumference;
  return (
    <div
      className={`score-ring tone-${verdictTone(report.verdict)} ${
        compact ? "is-compact" : ""
      }`}
      aria-label={`Risk score ${report.score} out of 100`}
    >
      <svg viewBox="0 0 104 104" aria-hidden="true">
        <circle className="score-track" cx="52" cy="52" r={radius} />
        <circle
          className="score-value"
          cx="52"
          cy="52"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="score-number">
        <strong>{report.score}</strong>
        {!compact && <span>/100</span>}
      </div>
    </div>
  );
}


export function SummaryView({
  report,
  onOpenFinding,
  onNavigate,
}: {
  report: ScanReport;
  onOpenFinding: (finding: Finding) => void;
  onNavigate: (view: WorkspaceView) => void;
}) {
  const behaviorCategories = [
    ["Execution", "execution", Terminal],
    ["Network", "network", Network],
    ["Credentials", "credentials", LockKeyhole],
    ["Evasion", "evasion", Eye],
    ["Persistence", "persistence", RefreshCw],
    ["Collection", "collection", Database],
  ] as const;
  const breakdown = Object.entries(report.archiveBreakdown);
  const maxBreakdown = Math.max(1, ...breakdown.map(([, count]) => count));
  const topFindings = report.findings.slice(0, 5);
  const topActions = report.actions.slice(0, 6);

  return (
    <div className="view-stack">
      <section className={`verdict-banner tone-${verdictTone(report.verdict)}`}>
        <div className="verdict-icon">
          {verdictIcon(report.verdict)}
        </div>
        <div className="verdict-message">
          <div className="section-kicker">ANALYST VERDICT</div>
          <h2>{report.verdict}</h2>
          <p>{verdictCopy(report)}</p>
        </div>
        <ScoreRing report={report} />
      </section>

      {/* Directly under the verdict, and above the results it qualifies. The
          YARA view has its own "did not run" panel, but the rest of these
          caveats used to exist only in the report object, so an analyst
          reading this screen saw a clean-looking scan. */}
      {report.warnings.length > 0 && (
        <section className="panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">COVERAGE CAVEATS</div>
              <h3>
                {report.warnings.length} item
                {report.warnings.length === 1 ? "" : "s"} did not run, or ran
                without full coverage
              </h3>
            </div>
            <span>
              This scan is a statement about what was examined, not a clean bill
              of health.
            </span>
          </div>
          <div className="container-warnings">
            {report.warnings.map((warning) => (
              <span key={warning}>
                <Info size={13} />
                {warning}
              </span>
            ))}
          </div>
        </section>
      )}

      {report.familyTags.length > 0 && (
        <section className="signal-line">
          <span>THREAT PROFILE</span>
          <div>
            {report.familyTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => onNavigate("findings")}
              >
                <Zap size={12} />
                {tag}
              </button>
            ))}
          </div>
        </section>
      )}

      {(report.knownHashMatches.length > 0 ||
        report.fingerprintMatches.length > 0 ||
        report.familyMatches.length > 0) && (
        <section className="panel threat-identity-panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">THREAT INTELLIGENCE</div>
              <h3>Identity matches</h3>
            </div>
            <span>
              {[
                report.knownHashMatches.length
                  ? `${report.knownHashMatches.length} exact`
                  : "",
                report.fingerprintMatches.length
                  ? `${report.fingerprintMatches.length} repack`
                  : "",
                report.familyMatches.length
                  ? `${report.familyMatches.length} signature`
                  : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </div>
          <div className="threat-match-grid">
            {report.knownHashMatches.slice(0, 4).map((match) => (
              <article className="threat-match is-exact" key={match.id}>
                <span className="threat-match-icon">
                  <Fingerprint size={17} />
                </span>
                <div>
                  <span>EXACT {match.algorithm.toUpperCase()} MATCH</span>
                  <strong>
                    {match.family} · {match.variant}
                  </strong>
                  <code>{truncateMiddle(match.hash, 16, 12)}</code>
                  <small>
                    {match.entry ?? match.target} · {match.source}
                  </small>
                </div>
              </article>
            ))}
            {report.fingerprintMatches.slice(0, 4).map((match) => (
              <article className="threat-match is-repack" key={match.id}>
                <span className="threat-match-icon">
                  <Copy size={17} />
                </span>
                <div>
                  <span>
                    REPACK MATCH · {match.similarity.toFixed(1)}%
                  </span>
                  <strong>
                    {match.family} · {match.variant}
                  </strong>
                  <code>{match.simhash64}</code>
                  <small>
                    threshold {match.threshold.toFixed(1)}% · {match.source}
                  </small>
                </div>
              </article>
            ))}
            {report.familyMatches.slice(0, 6).map((match) => (
              <article className="threat-match" key={match.signatureId}>
                <span className="threat-match-icon">
                  <Radar size={17} />
                </span>
                <div>
                  <span>
                    {match.confidence.toUpperCase()} CONFIDENCE ·{" "}
                    {match.score}/{match.maxScore}
                  </span>
                  <strong>
                    {match.family} · {match.variant}
                  </strong>
                  <small>
                    {match.matchedSignals.length} independent static signals
                  </small>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {topActions.length > 0 && (
        <section className="panel observed-actions-panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">CONCRETE BEHAVIOR</div>
              <h3>Observed actions</h3>
            </div>
            <button
              className="text-button"
              type="button"
              onClick={() => onNavigate("actions")}
            >
              View all {report.actions.length}
              <ChevronRight size={14} />
            </button>
          </div>
          <div className="observed-action-grid">
            {topActions.map((action) => (
              <button
                type="button"
                key={action.id}
                onClick={() => onNavigate("actions")}
              >
                <span className={`action-glyph tone-${action.severity}`}>
                  {action.category === "download" ? (
                    <Download size={16} />
                  ) : action.category === "execution" ||
                    action.category === "command" ? (
                    <Terminal size={16} />
                  ) : action.category === "network" ? (
                    <Network size={16} />
                  ) : action.category === "credential" ? (
                    <LockKeyhole size={16} />
                  ) : (
                    <ScanLine size={16} />
                  )}
                </span>
                <span>
                  <strong>{action.title}</strong>
                  <small title={action.entry}>
                    {action.method
                      ? `${action.entry} · ${action.method}`
                      : action.entry}
                  </small>
                </span>
                <span className={`severity-label tone-${action.severity}`}>
                  {action.severity}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="summary-grid">
        <section className="panel span-two">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">DETECTION COVERAGE</div>
              <h3>Behavior map</h3>
            </div>
            <span>{report.findings.length} total signals</span>
          </div>
          <div className="behavior-grid">
            {behaviorCategories.map(([label, category, Icon]) => {
              const findings = report.findings.filter(
                (finding) => finding.category === category,
              );
              const topSeverity =
                severityOrder.find((severity) =>
                  findings.some((finding) => finding.severity === severity),
                ) ?? "info";
              return (
                <button
                  key={category}
                  className={findings.length ? "has-signal" : ""}
                  type="button"
                  onClick={() => onNavigate("findings")}
                >
                  <span className={`behavior-icon tone-${topSeverity}`}>
                    <Icon size={17} />
                  </span>
                  <span>
                    <strong>{label}</strong>
                    <small>
                      {findings.length
                        ? `${findings.length} signal${findings.length === 1 ? "" : "s"}`
                        : "No signal"}
                    </small>
                  </span>
                  <StatusDot severity={findings.length ? topSeverity : "safe"} />
                </button>
              );
            })}
          </div>
        </section>

        <section className="panel archive-makeup">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">CONTENTS</div>
              <h3>Archive makeup</h3>
            </div>
          </div>
          <div className="breakdown-list">
            {breakdown.map(([label, count]) => (
              <div key={label}>
                <div>
                  <span>{label}</span>
                  <strong>{count.toLocaleString()}</strong>
                </div>
                <span className="bar-track">
                  <span style={{ width: `${(count / maxBreakdown) * 100}%` }} />
                </span>
              </div>
            ))}
          </div>
          <div className="archive-total">
            <span>Expanded size</span>
            <strong>{formatBytes(report.totalUncompressed)}</strong>
          </div>
        </section>

        <section className="panel ai-probability-card">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">CODE PROVENANCE</div>
              <h3>AI-likelihood signals</h3>
            </div>
            <Bot size={17} />
          </div>
          <button type="button" onClick={() => onNavigate("quality")}>
            <span
              className="ai-probability-gauge"
              style={{
                background: `conic-gradient(var(--accent) ${report.codeQuality.score * 3.6}deg, var(--line) 0deg)`,
              }}
              aria-label={`${report.codeQuality.score} out of 100 AI-likelihood signal score`}
            >
              <span>
                <strong>{report.codeQuality.score}</strong>
                <small>/100</small>
              </span>
            </span>
            <span className="ai-probability-copy">
              <strong>{report.codeQuality.label}</strong>
              <small>
                {report.codeQuality.signals.length
                  ? `${report.codeQuality.signals.length} evidence-backed signal${report.codeQuality.signals.length === 1 ? "" : "s"}`
                  : "No meaningful generated-code markers"}
              </small>
              <em>Probability estimate, not authorship proof</em>
            </span>
            <ChevronRight size={15} />
          </button>
        </section>
      </div>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">PRIORITY QUEUE</div>
            <h3>Top findings</h3>
          </div>
          <button
            className="text-button"
            type="button"
            onClick={() => onNavigate("findings")}
          >
            View all
            <ChevronRight size={14} />
          </button>
        </div>
        {topFindings.length ? (
          <div className="finding-rows">
            {topFindings.map((finding) => (
              <button
                type="button"
                key={finding.id}
                onClick={() => onOpenFinding(finding)}
              >
                <span className={`severity-rail tone-${finding.severity}`} />
                <span className={`severity-label tone-${finding.severity}`}>
                  {finding.severity}
                </span>
                <span className="finding-row-copy">
                  <strong>{finding.title}</strong>
                  <small>{finding.entry}</small>
                </span>
                <span className="rule-code">{finding.ruleId}</span>
                <ChevronRight size={15} />
              </button>
            ))}
          </div>
        ) : (
          <div className="empty-panel">
            <ShieldCheck size={25} />
            <strong>No detection rules matched</strong>
            <span>Manual review is still required for unknown behavior.</span>
          </div>
        )}
      </section>

      <section className="panel scan-facts-panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">RUN DETAILS</div>
            <h3>Analysis record</h3>
          </div>
        </div>
        <div className="fact-table">
          <div>
            <span>Engine</span>
            <strong>{report.analyzerVersion}</strong>
          </div>
          <div>
            <span>Completed</span>
            <strong>{formatDate(report.completedAt)}</strong>
          </div>
          <div>
            <span>Duration</span>
            <strong>{formatDuration(report.durationMs)}</strong>
          </div>
          <div>
            <span>Signature</span>
            <strong>{report.signed ? "Signed archive" : "Not verified"}</strong>
          </div>
          <div>
            <span>Compression ratio</span>
            <strong>{report.compressionRatio.toFixed(1)}×</strong>
          </div>
          <div>
            <span>Container</span>
            <strong>{report.containerType}</strong>
          </div>
        </div>
      </section>
    </div>
  );
}
